import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import { Input } from 'src/shared/components/ui/input';
import useCanAdministerOrganisations from 'src/app/admin/hooks/useCanAdministerOrganisations';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { GNPViews } from 'src/routes/routesConfig';
import { Customer } from 'src/shared/types';
import ManageOrganisationSheet from 'src/app/admin/components/manage-organisation-sheet/ManageOrganisationSheet';
import DeleteOrganisationDialog from 'src/app/admin/components/delete-organisation-dialog/DeleteOrganisationDialog';
import OrganisationProductsSheet from 'src/app/admin/components/organisation-products-sheet/OrganisationProductsSheet';
import { MANAGE_PRODUCTS_PARAM } from 'src/shared/data/manageProductsParam';
import OrgRowActions from './components/org-row-actions/OrgRowActions';

type Organisation = Pick<Customer, 'customerId' | 'customerName'>;

type OrgManagementTableProps = {
    organisations: Organisation[];
};

/** Row actions navigate or open a drawer themselves; let them win over the row's own handler. */
const stopRowActivation = (event: React.MouseEvent) => event.stopPropagation();

type RowActionHandlers = {
    canManage: boolean;
    onRename: (organisation: Organisation) => void;
    canDelete: boolean;
    onDelete: (organisation: Organisation) => void;
    onManageProducts: (organisation: Organisation) => void;
};

const buildColumns = ({ canManage, onRename, canDelete, onDelete, onManageProducts }: RowActionHandlers): DataTableColumns => [
    { columnId: 'customerId', title: 'Customer ID', isSortable: true, isSearchable: true },
    {
        columnId: 'customerName',
        title: 'Customer Name',
        isSortable: true,
        isSearchable: true,
        renderer: ({ rowData }: { rowData: TableRowData }) => {
            const customerId = rowData[0] as string;
            const customerName = rowData[1] as string;
            return (
                <Link to={navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId })} onClick={stopRowActivation}>
                    {customerName}
                </Link>
            );
        },
    },
    {
        columnId: 'actions',
        title: 'Actions',
        renderer: ({ rowData }: { rowData: TableRowData }) => (
            <OrgRowActions
                organisation={{ customerId: rowData[0] as string, customerName: rowData[1] as string }}
                canManage={canManage}
                onRename={onRename}
                canDelete={canDelete}
                onDelete={onDelete}
                onManageProducts={onManageProducts}
            />
        ),
    },
];

const OrgManagementTable = ({ organisations }: OrgManagementTableProps) => {
    const [searchText, setSearchText] = useState('');
    const [renamingOrganisation, setRenamingOrganisation] = useState<Organisation | null>(null);
    const [deletingOrganisation, setDeletingOrganisation] = useState<Organisation | null>(null);
    const [productsOrganisation, setProductsOrganisation] = useState<Organisation | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // An unknown id opens nothing rather than an empty flyout: the link is
    // stale or hand-typed, and the list behind it is still useful.
    const linkedProductsOrganisation = organisations.find(org => org.customerId === searchParams.get(MANAGE_PRODUCTS_PARAM)) ?? null;
    const openProductsFor = productsOrganisation ?? linkedProductsOrganisation;

    const closeProducts = () => {
        setProductsOrganisation(null);
        if (searchParams.has(MANAGE_PRODUCTS_PARAM)) {
            // Drop the param too, or reopening from a row would fight the URL.
            const remaining = new URLSearchParams(searchParams);
            remaining.delete(MANAGE_PRODUCTS_PARAM);
            setSearchParams(remaining, { replace: true });
        }
    };
    const { canManage, canDelete } = useCanAdministerOrganisations();

    const data = useMemo(() => organisations.map(org => [org.customerId, org.customerName, null]), [organisations]);
    const columns = useMemo(
        () =>
            buildColumns({
                canManage,
                onRename: setRenamingOrganisation,
                canDelete,
                onDelete: setDeletingOrganisation,
                onManageProducts: setProductsOrganisation,
            }),
        [canManage, canDelete]
    );

    const openOrganisationPage = (rowData: TableRowData) =>
        navigate(navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId: rowData[0] as string }));

    return (
        <div className='flex flex-col gap-4'>
            <div className='relative w-80' data-testid='org-management-search'>
                <SearchIcon className='w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                <Input
                    roundness='Round'
                    type='search'
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder='Search organisations ...'
                    aria-label='Search organisations'
                    data-testid='org-management-search-input'
                    className='pl-9'
                />
            </div>
            <div className='[&_tbody_tr]:cursor-pointer'>
                <DataTable
                    columns={columns}
                    state={{ data, searchText }}
                    onRowSelect={openOrganisationPage}
                    emptyStateMessage='No organisations to display.'
                />
            </div>
            {renamingOrganisation && (
                <ManageOrganisationSheet
                    open
                    onOpenChange={isOpen => !isOpen && setRenamingOrganisation(null)}
                    customerId={renamingOrganisation.customerId}
                    customerName={renamingOrganisation.customerName}
                />
            )}
            {openProductsFor && (
                <OrganisationProductsSheet
                    open
                    onOpenChange={isOpen => !isOpen && closeProducts()}
                    customerId={openProductsFor.customerId}
                    customerName={openProductsFor.customerName}
                />
            )}
            {deletingOrganisation && (
                <DeleteOrganisationDialog
                    open
                    onOpenChange={isOpen => !isOpen && setDeletingOrganisation(null)}
                    customerId={deletingOrganisation.customerId}
                    customerName={deletingOrganisation.customerName}
                />
            )}
        </div>
    );
};

export default OrgManagementTable;
